import React from 'react';
import PropTypes from 'prop-types';
import { customerInfo as customerInfoShape, userShape } from 'constants/propTypes';
import { FormattedMessage } from 'react-intl';
import { useQuery } from '@apollo/client';
import { Spinner } from 'admin-components/components';
import AgentPortalError from 'errors/AgentPortalError';
import { PROFILE_LOYALTY_MEMBER_TIER_HISTORY } from './graphql';
import { SectionContainer, SectionTableContainer, StyledTableCell, StyledTableHeader, StyledTableRow } from '../ClubEurostarMember.styles';
import { Column, Row } from '../../../components/shared/Layout';
import moment from 'moment';

const TierHistoryRow = ({ date, user, oldTier, newTier, reason, details }) => (
  <StyledTableRow columns={6}>
    <StyledTableCell span={1}>{date}</StyledTableCell>
    <StyledTableCell span={1} justifyContent="center">
      {user}
    </StyledTableCell>
    <StyledTableCell span={1} justifyContent="center">
      {oldTier}
    </StyledTableCell>
    <StyledTableCell span={1} justifyContent="center">
      {newTier}
    </StyledTableCell>
    <StyledTableCell span={1} justifyContent="center">
      {reason}
    </StyledTableCell>
    <StyledTableCell span={1} justifyContent="center">
      {details}
    </StyledTableCell>
  </StyledTableRow>
);

TierHistoryRow.propTypes = {
  date: PropTypes.string,
  user: PropTypes.string,
  oldTier: PropTypes.number,
  newTier: PropTypes.number,
  reason: PropTypes.number,
  details: PropTypes.number
};

const TierHistory = ({ customerInfo }) => {
  const membershipNumber = customerInfo.profile?.loyalty?.membershipNumber;

  const variables = {
    membershipNumber
  };

  const { loading, data, error } = useQuery(PROFILE_LOYALTY_MEMBER_TIER_HISTORY, {
    variables,
    fetchPolicy: 'network-only'
  });

  if (error) {
    throw new AgentPortalError(error);
  }

  const sortedTierHistory = data?.profileLoyaltyMemberTierHistory
    ? [...data.profileLoyaltyMemberTierHistory].sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate))
    : [];

  if (loading) {
    return (
      <SectionContainer>
        <Row span={16} height="10vh" data-testid="full-page-spinner">
          <Column justifyContent="center" alignItems="center">
            <Spinner />
          </Column>
        </Row>
      </SectionContainer>
    );
  }

  return (
    <SectionContainer>
      <SectionTableContainer data-testid={`tier-history-table`}>
        <StyledTableRow columns={6}>
          <StyledTableHeader span={1} justifyContent="center">
            <FormattedMessage id="clubEurostar.tierHistory.date" tagName="strong" />
          </StyledTableHeader>
          <StyledTableHeader span={1} justifyContent="center">
            <FormattedMessage id="clubEurostar.tierHistory.user" tagName="strong" />
          </StyledTableHeader>
          <StyledTableHeader span={1} justifyContent="center">
            <FormattedMessage id="clubEurostar.tierHistory.oldTier" tagName="strong" />
          </StyledTableHeader>
          <StyledTableHeader span={1} justifyContent="center">
            <FormattedMessage id="clubEurostar.tierHistory.newTier" tagName="strong" />
          </StyledTableHeader>
          <StyledTableHeader span={1} justifyContent="center">
            <FormattedMessage id="clubEurostar.tierHistory.reason" tagName="strong" />
          </StyledTableHeader>
          <StyledTableHeader span={1} justifyContent="center">
            <FormattedMessage id="clubEurostar.tierHistory.details" tagName="strong" />
          </StyledTableHeader>
        </StyledTableRow>

        {sortedTierHistory.map((row) =>
          TierHistoryRow({
            date: moment(row.createdDate).format('DD-MMMM-YYYY'),
            user: row.createdByName,
            oldTier: row.oldTier,
            newTier: row.newTier,
            reason: row.changeReasonType ?? '',
            details: row.tierChangeReason ?? ''
          })
        )}
      </SectionTableContainer>
    </SectionContainer>
  );
};

TierHistory.propTypes = {
  customerInfo: customerInfoShape,
  user: userShape,
  canPerformOperations: PropTypes.bool,
  isLoungeStaffUser: PropTypes.bool
};

export default TierHistory;
